import { type AnyAction } from "../../../redux/types";
import { type ThunkAction, type ThunkDispatch } from "redux-thunk";
import { type RootState } from "../../../redux/store";
import { type CsticUpdate } from "../api/sendLinearConfiguratorUpdate";
import { type Kmat, type LinearConfiguration, type LinearConfigurationUpdate, type SubstepTile } from "../models";
import { type SavedConfigurationDTO, SubstepType } from "../dtos";
import debounce from "lodash/debounce";
import { type LinearConfiguratorState } from "./types";
import { csticUpdatesFromDict } from "../utils";
import {
  CSTIC_AL_NAME,
  CSTIC_AR_NAME,
  CSTIC_ENABLE_ALAR_NAME,
  CSTIC_ENABLE_ALAR_VALUE_DISABLE,
  CSTIC_ENABLE_ALAR_VALUE_ENABLE,
  CSTIC_LENGTH_NAME,
  CSTIC_PART_AMOUNT_NAME,
  CSTIC_PART_SIZING_TYPE_NAME,
  CSTIC_PART_SIZING_TYPE_NONE,
  CSTIC_VHOE,
  CSTIC_VHOE_DEFAULT_VALUE,
  CSTIC_VSKITS_CUSTOM_VALUE,
  CSTIC_VSKITS_NAME,
  DEFAULT_CARRIAGES_NUMBER,
  DEFAULT_CARRIAGES_NUMBER_CSTIC,
  DEFAULT_GUIDEWAYS_NUMBER,
  DEFAULT_GUIDEWAYS_NUMBER_CSTIC,
  FAKE_CSTIC_UNIT_LENGTH,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
  getEditCarriageCsticsToReset,
  GUIDEWAY_PIECE_PATTERN,
  INPUT_VALIDATION_DELAY,
  MAX_PIECE_AMOUNT_IN_KB,
} from "../constants";
import { getConfigId, getCurrentStep, getProductCode, isCurrentStepValid } from "./selectors";
import { sendLinearSaveConfig } from "../api/sendLinearSaveConfig";
import { ConfiguratorType } from "../../ConfiguratorType";
import { LOCATING_FACE_CODE_KEY } from "../../Rail/v2/configuration/classic/railConfiguratorConstants";
import { trackLCPreviousClick, trackLCStepperClick, trackLCStepStarted } from "../../common/configuratorGA";
import { sendLinearDetailsReset } from "../api/sendLinearDetailsReset";
import { getExtraValidationParamName } from "~/Configurators/Linear/namingUtils";
import { LcSummaryTab } from "~/Configurators/common/LcSummaryTab";
import { getConfiguratorDefinition } from "~/Configurators/configuratorRegistry";
import { KmatType } from "~/Configurators/Linear/getKmatType";
import { getNextStep, getPreviousStep } from "~/Configurators/linearStepHelper";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { selectCsticExpandedFromNested } from "~/Configurators/Linear/redux/selectCstic";
import { RAIL_CONFIGURATOR_PRODUCT_CODE, UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export const LOAD_DATA = "Linear: Load";
export const UPDATE_CSTICS = "Linear: Update characteristic values";
export const UPDATE_ADDITIONAL_DATA = "Linear: Update substep additional data";
export const UPDATE_CSTICS_FROM_TILE = "Linear: Update characteristic values from a clickable tile";
export const RESET_CURRENT_STEP = "Linear: Reset current step";
export const VALIDATE_CURRENT_STEP = "Linear: Validate current step";
export const SET_STEP = "Linear: Set step";
export const SET_TAB = "Linear: Set active tab";
export const CUSTOM_SEGMENT_LENGTH_SELECTED = "Linear: custom segment length selected";
export const CUSTOM_SEGMENT_LENGTH_SELECTED_RESET = "Linear: reset custom segment length selected";
export const UPDATE_SMDS_FLIP = "Linear: update SMDS flip";
export const POST_HYBRIS_UPDATE = "Linear: Synchronize with hybris post update";

export const RESTORE_SNAPSHOT = "Linear: Restore previous valid snapshot";

export const TOGGLE_LINEAR_CARRIAGE_EDIT = "Linear: Toggle Carriage edit";

export const SHOW_LOADER = "Linear: Show loader";
export const HIDE_LOADER = "Linear: Hide loader";
export const UPDATE_QUANTITY = "Linear: Update quantity";
export const UPDATE_PRICE_REFERENCE = "Linear: Price Reference";

export const MARK_ALREADY_ADDED_TO_CART = "Linear: Mark added to cart";

export function showLoader() {
  return {
    type: SHOW_LOADER,
  };
}

export function hideLoader() {
  return {
    type: HIDE_LOADER,
  };
}

export function loadData(data: LinearConfiguration, helpUrl: string, type: ConfiguratorType) {
  return {
    type: LOAD_DATA,
    payload: {
      data,
      helpUrl,
      type,
    },
  };
}

export function setAlreadyAddedToCart(newConfigId: string | null | undefined) {
  return {
    type: MARK_ALREADY_ADDED_TO_CART,
    payload: newConfigId,
  };
}

export function restorePreviousSnapshot(): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: RESTORE_SNAPSHOT,
    });

    const productCode = getState().linearConfigurator.productCode;
    const resetStepApi = getConfiguratorDefinition(productCode).apiCalls.stepReset;

    dispatch(sendUpdateToHybris(resetStepApi));
  };
}

export function updateCsticValuesFromTile(
  elementType: SubstepType,
  tileIndex: number | null,
  tile: SubstepTile,
): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_CSTICS_FROM_TILE,
      payload: { elementType, tileIndex, tile },
    });

    const productCode = getState().linearConfigurator.productCode;
    const updateApi = getConfiguratorDefinition(productCode).apiCalls.update;

    const updates = [...csticUpdatesFromDict(tile.cstics), ...csticUpdatesFromDict(tile.csticsToSetOnly ?? {})];

    dispatch(sendUpdateToHybris((state) => updateApi(state, updates)));
  };
}

export function updateCsticValuesOld(
  updates: Dict<string | undefined | null>,
  owningSubstep: SubstepType,
): ThunkResult<void> {
  return updateCsticValues(csticUpdatesFromDict(updates), owningSubstep);
}

export function updateCsticValues(updates: CsticUpdate[], owningSubstep: SubstepType): ThunkResult<void> {
  return (dispatch, getState) => {
    const filtered = updates.filter((update) => !!update.key);
    if (filtered.length !== updates.length) {
      console.warn("Some updates were filtered out because of missing keys", updates);
    }
    dispatch(updateCsticsAction(filtered, owningSubstep));

    const linearState = getState().linearConfigurator;
    const productCode = linearState.productCode;
    if (
      updates.every((update) => {
        const cstic = selectCsticExpandedFromNested(
          { ...update, index: update.index ?? 0 },
          owningSubstep,
        )(linearState);
        return cstic.cstic.fake;
      })
    ) {
      const miscUpdateApi = getConfiguratorDefinition(productCode).apiCalls.miscChange;
      dispatch(sendUpdateToHybris(miscUpdateApi));
    } else {
      const updateApi = getConfiguratorDefinition(productCode).apiCalls.update;
      dispatch(sendUpdateToHybris((state) => updateApi(state, filtered)));
    }
  };
}

export function resetCurrentStep(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();

    const currentStep = getCurrentStep(state);

    // SCHAEFF-31278 - Reset in step 0 should do the same as the button on top
    if (currentStep === 0) {
      dispatch(resetConfiguratorAction());
      return;
    }

    const configuratorType = state.linearConfigurator.type!;
    const productCode = state.linearConfigurator.productCode;
    const resetStepApi = getConfiguratorDefinition(productCode).apiCalls.stepReset;

    // Step 6 - guideway set
    if (configuratorType === ConfiguratorType.System && currentStep === 5) {
      const linearState = state.linearConfigurator;
      const mainKmat = getMainKmat({ kmats: linearState.kmats });
      const lengthCstic = mainKmat?.cstics[FAKE_CSTIC_UNIT_LENGTH];

      const updates = linearState.kmats[KmatType.GUIDEWAY]
        .map((kmat, index) => {
          return prepareResetGuidewaySetUpdates(kmat, index, lengthCstic?.value ?? null, false);
        })
        .flat();

      dispatch(resetSegmentLengthSelected());
      dispatch(updateCsticValues(updates, SubstepType.GuidewaySet));
    } else if (configuratorType === ConfiguratorType.System && currentStep === 6) {
      dispatch({
        type: RESET_CURRENT_STEP,
      });
      const linearState = state.linearConfigurator;
      const updates = linearState.kmats[KmatType.CARRIAGE]
        .map((kmat, index) => {
          return prepareResetCarriageDetailsUpdates(kmat, index);
        })
        .flat();
      updates.push({
        type: KmatType.DEFAULT,
        key: CSTIC_VHOE,
        value: CSTIC_VHOE_DEFAULT_VALUE,
      });
      dispatch({ type: TOGGLE_LINEAR_CARRIAGE_EDIT, payload: { carriageIndex: undefined, statusEdit: false } });
      dispatch(updateCsticsAction(updates, SubstepType.CarriageDetails));
      dispatch(sendUpdateToHybris(resetStepApi));
    } else if (configuratorType === ConfiguratorType.Rail && currentStep === 0) {
      dispatch(resetConfiguratorAction()); //it's a step 1, so just resetting everything works fine
    } else if (
      configuratorType === ConfiguratorType.Rail &&
      productCode === RAIL_CONFIGURATOR_PRODUCT_CODE &&
      currentStep === 3
    ) {
      dispatch({
        type: RESET_CURRENT_STEP,
      });
      dispatch(resetSegmentLengthSelected());
      const linearState = state.linearConfigurator;
      const mainKmat = getMainKmat({ kmats: linearState.kmats });
      const lengthCstic = mainKmat?.cstics[FAKE_CSTIC_UNIT_LENGTH];

      const updates = linearState.kmats[KmatType.GUIDEWAY]
        .map((kmat, index) => {
          return prepareResetGuidewaySetUpdates(kmat, index, lengthCstic?.value ?? null, true);
        })
        .flat();
      updates.push({
        type: KmatType.GUIDEWAY,
        key: LOCATING_FACE_CODE_KEY,
        value: linearState.kmats[KmatType.GUIDEWAY][0].cstics[LOCATING_FACE_CODE_KEY]?.values.filter(
          ($) => $.standard,
        )[0]?.value,
      });

      dispatch(updateCsticValues(updates, SubstepType.ConfigureRail));
    } else if (configuratorType === ConfiguratorType.System && currentStep === 0) {
      const linearState = state.linearConfigurator;

      const updates = linearState.kmats[KmatType.DEFAULT]
        .map((kmat, index) => {
          return prepareResetSystemLayoutUpdates(kmat, index);
        })
        .flat();

      dispatch(updateCsticValues(updates, SubstepType.SystemLayout));
    } else {
      dispatch({
        type: RESET_CURRENT_STEP,
      });

      dispatch(sendUpdateToHybris(resetStepApi));
    }
  };
}

export function sendUpdateToHybris(
  updateFunc: (state: LinearConfiguratorState) => Promise<LinearConfigurationUpdate>,
  navigationUpdate?: boolean,
): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch(showLoader());

    try {
      const updateJson = await updateFunc(getState().linearConfigurator);
      dispatch({
        type: POST_HYBRIS_UPDATE,
        payload: {
          hybrisData: updateJson,
          navigationUpdate,
        },
      });
    } catch (e) {
      console.error(e);
      dispatch(hideLoader());
    }
  };
}

/**
 * Goes to the next step. First validates data in current step
 */
export function nextStep(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState().linearConfigurator;
    const nextStep = getNextStep(state.type!, state.currentStep, state.steps, state.nextSteps);
    if (!nextStep) {
      dispatch({
        type: VALIDATE_CURRENT_STEP,
      });
      return;
    }
    if (state.highTideStep === state.currentStep) {
      // index from model is 1 based, while in state it's 0 based
      trackLCStepStarted(state.productCode, state.currentStep + 1);
    }
    dispatch(internalGoToStep(state.steps.indexOf(nextStep), "forward"));
  };
}

export function previousStep(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState().linearConfigurator;
    const previousStep = getPreviousStep(state.type!, state.currentStep, state.steps, state.previousSteps);
    if (!previousStep) {
      throw new Error("No previous step found");
    }
    dispatch(internalGoToStep(state.steps.indexOf(previousStep), "back"));
  };
}

export function goToStep(index: number): ThunkResult<void> {
  return internalGoToStep(index, "stepper");
}

export function goToCadSummary(): ThunkResult<void> {
  return (dispatch, getState) => {
    const activeTab = getState().linearConfigurator.activeTab;
    const steps = getState().linearConfigurator.steps;
    if (activeTab !== LcSummaryTab.CAD) {
      dispatch(setLCSummaryTabAction(LcSummaryTab.CAD));
    }
    dispatch(internalGoToStep(steps.length - 1, "stepper", false));
  };
}

export function setLCSummaryTabAction(tab: LcSummaryTab) {
  return {
    type: SET_TAB,
    payload: tab,
  } as const;
}

/**
 * Goes back to a previously completed step. No validation done
 * @param index Optional. Index of the step to go to. Otherwise go to the previous one
 */
function internalGoToStep(
  index: number,
  source: "stepper" | "back" | "forward",
  scrollToTop = true,
): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: VALIDATE_CURRENT_STEP,
    });
    const lcState = getState().linearConfigurator;
    const currentStep = lcState.currentStep;

    const forwardStep = currentStep + 1 === index;

    const isStepPossible =
      index >= 0 &&
      index < lcState.steps.length &&
      index !== lcState.currentStep &&
      (lcState.steps[index].active || lcState.currentStep + 1 === index);

    if (!isStepPossible) {
      return;
    }

    const currentHighTide = getState().linearConfigurator.highTideStep;
    const valid = isCurrentStepValid(getState());

    if (!valid) {
      // SCHAEFF-9563 - This should scroll to the first error on the page
      // Giving 16ms to make sure react manages to render out the html first.
      window.setTimeout(() => {
        document.querySelector('[data-message="error"], .Mui-error')?.scrollIntoView();
      }, 16);
      return;
    }

    dispatch(showLoader());

    settleNumberUpdatesPromise().then(async () => {
      dispatch({
        type: SET_STEP,
        payload: index,
      });

      if (scrollToTop) {
        window.scroll(0, 0);
      }

      const productCode = getState().linearConfigurator.productCode;
      const stepChangeApiCall = getConfiguratorDefinition(productCode).apiCalls.stepChange;

      dispatch(
        sendUpdateToHybris(
          stepChangeApiCall,
          !forwardStep || currentHighTide === getState().linearConfigurator.highTideStep,
        ),
      );

      if (source === "stepper" || source === "back") {
        // Index - target step
        const linearState = getState().linearConfigurator;
        const productCode = linearState.productCode;

        if (source === "stepper") {
          trackLCStepperClick(productCode, currentStep + 1, index + 1);
        } else if (source === "back") {
          trackLCPreviousClick(productCode, currentStep + 1, index + 1);
        }
      }

      if (index > currentHighTide) {
        // We have finished a step - notify Google Analytics
        for (let i = currentHighTide; i < index; i++) {
          await dispatch(getConfiguratorDefinition(productCode).stepHardcodedData[i].trackCompletedStepAction);
        }
      }
    });
  };
}

// Because there's no better name...
// Need to check these in Unit Configurator because sending out of bounds values causes endless loop.
// It's not caused in Rail configurator though.
// Probably needs the jar update from https://me.sap.com/case/002075129400007206802025/overview
const CSTICS_NOT_TO_SEND_IF_OUT_OF_BOUNDS = [
  (name: string) => name === CSTIC_AL_NAME,
  (name: string) => name === CSTIC_AR_NAME,
  (name: string) => GUIDEWAY_PIECE_PATTERN.test(name),
];

/*
 * Used so we don't spam the KB with every character inputted
 */
const __debouncedSendUpdate = debounce(
  async (
    substep: SubstepType,
    dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
    getState: () => RootState,
    updates?: CsticUpdate[],
  ) => {
    if (updates) {
      // Something else than step 3 length was sent
      const state = getState().linearConfigurator;

      let valid = true;
      if (state.productCode === UNIT_CONFIGURATOR_PRODUCT_CODE) {
        const sensitiveUpdates: CsticUpdate[] = [];
        updates.forEach((update) =>
          CSTICS_NOT_TO_SEND_IF_OUT_OF_BOUNDS.forEach((fun) => {
            if (fun(update.key)) {
              sensitiveUpdates.push(update);
            }
          }),
        );
        sensitiveUpdates.forEach((update) => {
          const extraParamName = getExtraValidationParamName(update);
          valid = valid && state.substeps[substep].additionalValidations[extraParamName] !== false;
        });
      }

      if (valid) {
        const productCode = getState().linearConfigurator.productCode;
        const updateApi = getConfiguratorDefinition(productCode).apiCalls.update;

        await dispatch(sendUpdateToHybris((state) => updateApi(state, updates)));
      } else {
        console.warn("Contains invalid values in senstivie cstics");
      }
    } else {
      if (import.meta.env.DEV) {
        console.warn("We shouldn't go into this path anymore?");
      }
      // Step 3 length was sent...
      const state = getState().linearConfigurator;
      const productCode = state.productCode;
      const miscUpdateApi = getConfiguratorDefinition(productCode).apiCalls.miscChange;
      await dispatch(sendUpdateToHybris(miscUpdateApi));
    }
    if (numberUpdatePromise && resolveNumberUpdatePromise) {
      resolveNumberUpdatePromise();
      resolveNumberUpdatePromise = null;
      numberUpdatePromise = null;
    }
  },
  INPUT_VALIDATION_DELAY,
);

export function flushPendingInputChanges() {
  window.setTimeout(() => __debouncedSendUpdate.flush(), 200);
}

// We don't have a registry of pending changes, so we need to fake some stuff.
// These promise and resolve are so the change step actions wait for number values to be sent first...
// ...this should be completely rewritten, but no idea how.
let numberUpdatePromise: Promise<unknown> | null = null;
let resolveNumberUpdatePromise: (() => void) | null = null;

const settleNumberUpdatesPromise = () => {
  return numberUpdatePromise ?? Promise.resolve();
};
/* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-function-type */
type ArgumentTypes<F extends Function> = F extends (...args: infer A) => any ? A : never;

const debouncedSendUpdate = (...args: ArgumentTypes<typeof __debouncedSendUpdate>) => {
  if (!numberUpdatePromise) {
    numberUpdatePromise = new Promise<void>((resolve) => {
      resolveNumberUpdatePromise = resolve;
      __debouncedSendUpdate(...args);
    });
  } else {
    __debouncedSendUpdate(...args);
  }
};

function customSegmentLengthSelected(value: boolean, kmatIndex: number): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: CUSTOM_SEGMENT_LENGTH_SELECTED,
      payload: { value, kmatIndex },
    });

    debouncedSendUpdate(SubstepType.GuidewaySet, dispatch, getState);
  };
}

function resetSegmentLengthSelected(): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: CUSTOM_SEGMENT_LENGTH_SELECTED_RESET,
    });

    debouncedSendUpdate(SubstepType.GuidewaySet, dispatch, getState);
  };
}

export function updateGuidewayPartType(
  kmatIndex: number,
  selectedValue: string,
  owningSubstep: SubstepType,
): ThunkResult<void> {
  return (dispatch, getState) => {
    const changes: CsticUpdate[] = [
      {
        type: KmatType.GUIDEWAY,
        value: selectedValue,
        key: CSTIC_PART_SIZING_TYPE_NAME,
        index: kmatIndex,
      },
    ];

    if (selectedValue === CSTIC_PART_SIZING_TYPE_NONE) {
      changes.push({
        type: KmatType.GUIDEWAY,
        value: null,
        key: CSTIC_PART_AMOUNT_NAME,
        index: kmatIndex,
      });
    }

    const hybrisUpdates = [...changes];
    const reduxUpdates = [...changes];

    const productCode = getState().linearConfigurator.productCode;
    const updateApi = getConfiguratorDefinition(productCode).apiCalls.update;

    dispatch(customSegmentLengthSelected(true, kmatIndex));
    dispatch(updateCsticsAction(reduxUpdates, owningSubstep));
    dispatch(sendUpdateToHybris((state) => updateApi(state, hybrisUpdates)));
  };
}

function prepareALARChanges(kmatIndex: number, csticName: typeof CSTIC_AL_NAME | typeof CSTIC_AR_NAME, val?: string) {
  const clearValue = val === undefined;

  const userUpdate: CsticUpdate = {
    index: kmatIndex,
    type: KmatType.GUIDEWAY,
    key: csticName,
    value: clearValue ? null : val!,
  };

  const enableUpdates: CsticUpdate = {
    index: kmatIndex,
    type: KmatType.GUIDEWAY,
    key: CSTIC_ENABLE_ALAR_NAME,
    value: clearValue ? CSTIC_ENABLE_ALAR_VALUE_DISABLE : CSTIC_ENABLE_ALAR_VALUE_ENABLE,
  };

  const clearUpdate: CsticUpdate = {
    index: kmatIndex,
    type: KmatType.GUIDEWAY,
    key: csticName === CSTIC_AL_NAME ? CSTIC_AR_NAME : CSTIC_AL_NAME,
    value: null,
  };

  return {
    reduxUpdates: [enableUpdates, userUpdate],
    hybrisUpdates: [enableUpdates, userUpdate, clearUpdate],
  };
}

export function updateALAR(
  kmatIndex: number,
  csticName: typeof CSTIC_AL_NAME | typeof CSTIC_AR_NAME,
  substep: SubstepType,
  val?: string,
): ThunkResult<void> {
  return (dispatch, getState) => {
    const { reduxUpdates, hybrisUpdates } = prepareALARChanges(kmatIndex, csticName, val);

    dispatch(updateCsticsAction(reduxUpdates, substep));
    debouncedSendUpdate(substep, dispatch, getState, hybrisUpdates);
  };
}

export function updateGuidewayLen(kmatIndex: number, substep: SubstepType, val?: number): ThunkResult<void> {
  return (dispatch, getState) => {
    const userUpdate: CsticUpdate = {
      index: kmatIndex,
      type: KmatType.GUIDEWAY,
      key: CSTIC_LENGTH_NAME,
      value: val === undefined ? null : val.toString(),
    };

    dispatch(updateCsticsAction([userUpdate], substep));

    debouncedSendUpdate(substep, dispatch, getState, [userUpdate]);
  };
}

export function updateSubstepAdditionalData(updates: CsticUpdate[], owningSubstep: SubstepType) {
  return {
    type: UPDATE_ADDITIONAL_DATA,
    payload: { updates, owningSubstep },
  };
}

export function updateGuidewayPieceLen(
  kmatIndex: number,
  pieceIndex: number,
  substep: SubstepType,
  val?: number,
  immediate?: boolean,
): ThunkResult<void> {
  return (dispatch, getState) => {
    const userUpdate: CsticUpdate = {
      index: kmatIndex,
      type: KmatType.GUIDEWAY,
      key: GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(pieceIndex + 1),
      value: val === undefined ? null : val.toString(),
    };

    dispatch(updateCsticsAction([userUpdate], substep));

    debouncedSendUpdate(substep, dispatch, getState, [userUpdate]);
    if (immediate) {
      flushPendingInputChanges();
    }
  };
}

export function refreshGuidewayPieceLen(
  kmatIndex: number,
  pieceIndex: number,
  substep: SubstepType,
  immediate?: boolean,
): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const kmat = state.linearConfigurator.kmats[KmatType.GUIDEWAY][kmatIndex];
    const cstic = kmat.cstics[GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(pieceIndex + 1)];
    dispatch(updateGuidewayPieceLen(kmatIndex, pieceIndex, substep, cstic.value ? +cstic.value : undefined, immediate));
  };
}

export function updateCsticsAction(updates: CsticUpdate[], owningSubstep: SubstepType) {
  return {
    type: UPDATE_CSTICS,
    payload: { updates, owningSubstep },
  };
}

export function prepareResetCarriageDetailsUpdates(carriageKmat: Kmat, kmatIndex: number): CsticUpdate[] {
  const updates: CsticUpdate[] = [];
  updates.push({
    index: kmatIndex,
    type: KmatType.CARRIAGE,
    key: CSTIC_VSKITS_NAME,
    value: CSTIC_VSKITS_CUSTOM_VALUE,
  });
  return updates;
}

function prepareResetGuidewaySetUpdates(
  guidewayKmat: Kmat,
  kmatIndex: number,
  lengthValue: string | null,
  clearLength: boolean,
): CsticUpdate[] {
  const updates: CsticUpdate[] = [];

  for (let i = 1; i <= MAX_PIECE_AMOUNT_IN_KB; i++) {
    const name = GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(i);
    if (guidewayKmat.cstics[name]) {
      updates.push({
        index: kmatIndex,
        type: KmatType.GUIDEWAY,
        key: GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(i),
        value: null,
      });
    }
  }

  updates.push({
    index: kmatIndex,
    type: KmatType.GUIDEWAY,
    key: CSTIC_PART_SIZING_TYPE_NAME,
    value: null,
  });
  updates.push({
    index: kmatIndex,
    type: KmatType.GUIDEWAY,
    key: CSTIC_PART_AMOUNT_NAME,
    value: null,
  });
  updates.push({
    index: kmatIndex,
    type: KmatType.GUIDEWAY,
    key: CSTIC_AL_NAME,
    value: null,
  });
  updates.push({
    index: kmatIndex,
    type: KmatType.GUIDEWAY,
    key: CSTIC_AR_NAME,
    value: null,
  });

  updates.push({
    index: kmatIndex,
    type: KmatType.GUIDEWAY,
    key: CSTIC_ENABLE_ALAR_NAME,
    value: CSTIC_ENABLE_ALAR_VALUE_DISABLE,
  });

  updates.push({
    index: kmatIndex,
    type: KmatType.GUIDEWAY,
    key: CSTIC_LENGTH_NAME,
    value: clearLength ? null : lengthValue,
  });

  return updates;
}

export function prepareResetSystemLayoutUpdates(defaultKmat: Kmat, kmatIndex: number): CsticUpdate[] {
  const updates: CsticUpdate[] = [];
  updates.push({
    index: kmatIndex,
    type: KmatType.DEFAULT,
    key: DEFAULT_GUIDEWAYS_NUMBER_CSTIC,
    value: DEFAULT_GUIDEWAYS_NUMBER,
  });
  updates.push({
    index: kmatIndex,
    type: KmatType.DEFAULT,
    key: DEFAULT_CARRIAGES_NUMBER_CSTIC,
    value: DEFAULT_CARRIAGES_NUMBER,
  });
  return updates;
}

export function resetGuidewaySet(kmatIndex: number, substep: SubstepType): ThunkResult<void> {
  return (dispatch, getState) => {
    const linearState = getState().linearConfigurator;
    const kmat = linearState.kmats[KmatType.GUIDEWAY][kmatIndex];
    const mainKmat = getMainKmat({ kmats: linearState.kmats });
    const lengthCstic = mainKmat?.cstics[FAKE_CSTIC_UNIT_LENGTH];
    const updates: CsticUpdate[] = prepareResetGuidewaySetUpdates(kmat, kmatIndex, lengthCstic?.value ?? null, false);

    dispatch(customSegmentLengthSelected(false, kmatIndex));
    dispatch(updateCsticValues(updates, substep));
  };
}

export function editLinearDetails(carriageIndex: number, substepType: SubstepType): ThunkResult<void> {
  return (dispatch, getState) => {
    const carriageKmats = getState().linearConfigurator.kmats[KmatType.CARRIAGE] || [];
    const configuratorType = getState().linearConfigurator.type!;
    dispatch({ type: TOGGLE_LINEAR_CARRIAGE_EDIT, payload: { carriageIndex: carriageIndex, statusEdit: true } });
    dispatch(
      updateCsticValues(prepareEditCarriageDetailsUpdates(carriageIndex, carriageKmats, configuratorType), substepType),
    );
  };
}

export function prepareEditCarriageDetailsUpdates(
  carriageIndex: number,
  carriageKmats: Kmat[],
  configuratorType: ConfiguratorType,
) {
  const updates: CsticUpdate[] = [];
  carriageKmats.map((kmat, kmatIndex) => {
    if (kmatIndex == carriageIndex) {
      getEditCarriageCsticsToReset(configuratorType).forEach((csticKey) => {
        if (kmat.cstics && kmat.cstics[csticKey]) {
          updates.push({
            type: KmatType.CARRIAGE,
            value: "",
            key: csticKey,
            index: carriageIndex,
          });
        }
      });
    }
  });
  updates.push();
  return updates;
}

export function updateQuantity(newQ: number) {
  return {
    type: UPDATE_QUANTITY,
    payload: newQ,
  };
}

export function updatePriceReference(newPR: string | undefined) {
  return {
    type: UPDATE_PRICE_REFERENCE,
    payload: newPR,
  };
}

export function resetConfiguratorAction(): ThunkResult<void> {
  return (dispatch, getState) => {
    const productCode = getState().linearConfigurator.productCode;
    const resetConfiguratorApiCall = getConfiguratorDefinition(productCode).apiCalls.reset;
    resetConfiguratorApiCall(productCode);
  };
}

export function saveConfiguratorAction(name: string): ThunkResult<Promise<SavedConfigurationDTO>> {
  return (dispatch, getState) => {
    const state = getState();
    const productCode = getProductCode(state);
    const configId = getConfigId(state);
    const currentStep = getCurrentStep(state);
    return sendLinearSaveConfig(productCode, configId, name, currentStep);
  };
}

export function resetLinearDetails(carriageIndex: number, statusEdit: boolean): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();

    const linearState = state.linearConfigurator;
    const updates = linearState.kmats[KmatType.CARRIAGE]
      .map((kmat, index) => {
        return prepareResetCarriageDetailsUpdates(kmat, index);
      })
      .flat();
    updates.push({
      type: KmatType.DEFAULT,
      key: CSTIC_VHOE,
      value: CSTIC_VHOE_DEFAULT_VALUE,
    });
    dispatch({ type: TOGGLE_LINEAR_CARRIAGE_EDIT, payload: { carriageIndex, statusEdit } });
    dispatch(updateCsticsAction(updates, SubstepType.CarriageDetails));

    const kmatId = linearState.kmats[KmatType.CARRIAGE][carriageIndex].parentId ?? "";
    dispatch(sendUpdateToHybris(sendLinearDetailsReset(kmatId)));
  };
}
